"use client";

import Image from "next/image";
import { CharityGlassPanel } from "@/components/ui/CharityGlassPanel";
import type { AboutFounderCard } from "@/lib/api-types";
import {
  normalizeFounderCardTemplate,
  type FounderCardTemplate,
} from "@/lib/founder-card-templates";
import { cn } from "@/lib/utils";

interface FounderQuoteCardProps {
  card: AboutFounderCard;
  className?: string;
  /** Compact preview for admin (smaller image). */
  preview?: boolean;
}

function PortraitBody({
  card,
  circle,
  preview,
}: {
  card: AboutFounderCard;
  circle?: boolean;
  preview?: boolean;
}) {
  const size = preview ? 120 : 180;
  return (
    <div className="flex flex-col items-center text-center">
      <Image
        src={card.imageSrc}
        alt={card.imageAlt || card.name}
        width={size}
        height={size}
        className={cn(
          "mb-5 border-4 border-[var(--charity-green)]/20 object-cover shadow-lg",
          circle ? "rounded-full" : "rounded-2xl",
          preview && "mb-3"
        )}
      />
      <h3
        className={cn(
          "font-display font-bold text-[var(--site-text)]",
          preview ? "text-base" : "text-xl"
        )}
      >
        {card.name}
      </h3>
      <p className="mt-1 text-sm font-semibold text-[var(--site-accent)]">
        {card.role}
      </p>
      <blockquote
        className={cn(
          "mt-4 italic leading-relaxed text-[var(--site-muted)]",
          preview ? "text-xs" : "text-sm"
        )}
      >
        &ldquo;{card.quote}&rdquo;
      </blockquote>
    </div>
  );
}

function renderTemplate(
  template: FounderCardTemplate,
  card: AboutFounderCard,
  preview?: boolean
) {
  switch (template) {
    case "split-quote":
      return (
        <div
          className={cn(
            "grid items-center gap-4 rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] p-4 shadow-sm md:grid-cols-[minmax(0,140px)_1fr] md:gap-6 md:p-6",
            preview && "md:grid-cols-[minmax(0,96px)_1fr] md:p-3"
          )}
        >
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[140px] overflow-hidden rounded-xl">
            <Image
              src={card.imageSrc}
              alt={card.imageAlt || card.name}
              fill
              className="object-cover object-top"
              sizes="140px"
            />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-[var(--site-text)] md:text-xl">
              {card.name}
            </h3>
            <p className="mt-1 text-sm font-semibold text-[var(--site-accent)]">
              {card.role}
            </p>
            <blockquote className="mt-3 text-sm italic leading-relaxed text-[var(--site-muted)]">
              &ldquo;{card.quote}&rdquo;
            </blockquote>
          </div>
        </div>
      );
    case "circle-soft":
      return (
        <div className="rounded-3xl border border-[var(--site-border)] bg-[var(--site-surface-alt)]/80 p-6 shadow-sm md:p-8">
          <PortraitBody card={card} circle preview={preview} />
        </div>
      );
    case "full-bleed":
      return (
        <article className="overflow-hidden rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] shadow-sm">
          <div className="relative aspect-[16/9] w-full">
            <Image
              src={card.imageSrc}
              alt={card.imageAlt || card.name}
              fill
              className="object-cover object-top"
              sizes="(max-width:768px) 100vw, 480px"
            />
          </div>
          <div className="p-5 text-center md:p-6">
            <h3 className="font-display text-xl font-bold text-[var(--site-text)]">
              {card.name}
            </h3>
            <p className="mt-1 text-sm font-semibold text-[var(--site-accent)]">
              {card.role}
            </p>
            <blockquote className="mt-3 text-sm italic leading-relaxed text-[var(--site-muted)]">
              &ldquo;{card.quote}&rdquo;
            </blockquote>
          </div>
        </article>
      );
    case "glass-portrait":
    default:
      return (
        <CharityGlassPanel revealDelay={preview ? 0 : 0.1}>
          <PortraitBody card={card} preview={preview} />
        </CharityGlassPanel>
      );
  }
}

export function FounderQuoteCard({
  card,
  className,
  preview = false,
}: FounderQuoteCardProps) {
  const template = normalizeFounderCardTemplate(card.template);
  return (
    <div className={className}>{renderTemplate(template, card, preview)}</div>
  );
}
